import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactComponent as AlignLeft } from '../../../../assets/align-left.svg';
import { ReactComponent as AlignMiddle } from '../../../../assets/align-middle.svg';
import { ReactComponent as AlignRight } from '../../../../assets/align-right.svg';
import { useState } from 'react';
import { CWTSchemaField, TextAlignment, TableRowStyles } from '../../../../types/custom-worksheet-v2';
import { useDispatch } from 'react-redux';
import { TooltipV2 } from '@cimplico/components';
import { formatMixedCase } from '../../../../utilities/formatting';
import { editField } from '../../store/slice';
import FieldHelperText from '../components/field/FieldHelperText';
import ToggleField from './ToggleField';

type AppearanceProps = {
    property: CWTSchemaField;
    tableRowStyles?: TableRowStyles;
};

function Appearance({ property, tableRowStyles }: AppearanceProps): JSX.Element {
    const dispatch = useDispatch();

    const [isCollapsed, setIsCollapsed] = useState(false);

    const alignments: TextAlignment[] = ['left', 'center', 'right'];

    function handleUpdateAlignment(alignment: TextAlignment): void {
        if (!tableRowStyles) {
            return;
        }

        dispatch(editField({ id: property.id, details: { styles: { ...tableRowStyles, alignment } } }));
    }

    return (
        <div className="flex flex-col">
            <div className="flex items-center pl-4 pt-4">
                <ChevronDownIcon
                    className={`hover:cursor-pointer w-4 h-4 -ml-4 text-gray-700 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    onClick={(): void => setIsCollapsed(!isCollapsed)}
                />
                <p className="text-sm font-bold leading-none text-gray-700">Appearance</p>
            </div>

            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                        className="overflow-hidden"
                    >
                        <div className="mt-4 flex flex-col gap-4 pl-4 pb-4 pr-0.5">
                            {/* HELPER TEXT */}
                            <FieldHelperText field={property} />
                            {/* IS THIS FIELD HIDDEN */}
                            <ToggleField
                                title="Hidden"
                                description="Hide this field from the user. It can still be used in calculations."
                                defaultEnabled={property.hidden ?? false}
                                onToggle={(): void => {
                                    console.log('Toggling to', !property.hidden);
                                    dispatch(editField({ id: property.id, details: { hidden: !property.hidden } }));
                                }}
                            />

                            {/* ABILITY TO PIN FIELD IN WS */}
                            <ToggleField
                                title="Pinning"
                                description="Allow the field to be pinned/unpinned in the worksheet."
                                defaultEnabled={property.canPin}
                                onToggle={(): void => {
                                    dispatch(editField({ id: property.id, details: { canPin: !property.canPin } }));
                                }}
                            />

                            {/* ABILITY TO RENAME FIELD IN WS */}
                            <ToggleField
                                title="Renaming"
                                description="Allow the field to be renamed in the worksheet."
                                defaultEnabled={property.canRename}
                                onToggle={(): void => {
                                    dispatch(editField({ id: property.id, details: { canRename: !property.canRename } }));
                                }}
                            />
                            {tableRowStyles && (
                                <div className="flex justify-between gap-2">
                                    <p className="text-gray-700 text-xs font-medium grow">Align</p>
                                    <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-md">
                                        {alignments.map((a) => {
                                            const isSelected = tableRowStyles.alignment === a;
                                            const icon = a === 'left' ? <AlignLeft /> : a === 'center' ? <AlignMiddle /> : <AlignRight />;

                                            return (
                                                <div key={a} className="py-1 px-3 relative" onClick={(): void => handleUpdateAlignment(a)}>
                                                    <div className="w-4 h-4 flex justify-center items-center shrink-0">
                                                        {isSelected && (
                                                            <motion.div
                                                                initial={false}
                                                                layoutId="text-align"
                                                                className="w-full h-full rounded-md bg-white absolute left-0"
                                                            />
                                                        )}
                                                        <div
                                                            className={`z-10 hover:cursor-pointer hover:text-gray-800 ${
                                                                isSelected ? 'text-gray-800' : 'text-gray-400'
                                                            }`}
                                                        >
                                                            <TooltipV2 message={formatMixedCase(a)} position="bottom" delayMs={750}>
                                                                {icon}
                                                            </TooltipV2>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Appearance;
